import { WaypointStatusName } from '@caresend/types';
import { getWaypointLetter, hasStatus } from '@caresend/utils';
import { defineComponent, computed } from 'vue';
import __vue_component__ from '../../ItineraryList/components/ExpandCopyID.vue.js';
import __vue_component__$3 from '../../ItineraryList/components/ItineraryCard/components/ItineraryDraftWaypointSupplyStatuses.vue.js';
import __vue_component__$1 from './ItineraryWaypointPopover.vue.js';
import { getTagInfoByFulfillmentStatus } from '../../Portal/components/SearchResults/orders/orders.js';
import { usePortalStore } from '../../../store/portal/index.js';
import __vue_component__$4 from '../../../../../symbols/StatusIndicator/StatusIndicator.vue.js';
import TransitionExpand from '../../../../../transitions/TransitionExpand/TransitionExpand.js';
import __vue_component__$2 from '../../../../../ui/Tooltip/Tooltip.vue.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import { hexAlphaTransform } from '../../../../../../functions/color.js';
import '../../../../../ui/Icon.vue.js';
import '../../../../../../functions/configuration/icons.js';
import '../../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../../functions/tracking/index.js';

var script = defineComponent({
    name: 'ItineraryWaypointCardStatuses',
    components: {
        ExpandCopyID: __vue_component__,
        ItineraryWaypointPopover: __vue_component__$1,
        Tooltip: __vue_component__$2,
        TransitionExpand,
        ItineraryDraftWaypointSupplyStatuses: __vue_component__$3,
        StatusIndicator: __vue_component__$4,
    },
    model: {
        prop: 'isCardCollapsed',
        event: 'setIsCollapsed',
    },
    props: {
        canCollapse: {
            type: Boolean,
            default: false,
        },
        isCardCollapsed: {
            type: Boolean,
            default: false,
        },
        waypoint: {
            type: Object,
            default: null,
        },
        dwPreview: {
            type: String,
            default: null,
        },
        inputDisabled: {
            type: Boolean,
            default: false,
        },
        statusOverride: {
            type: Object,
            default: () => null,
        },
        suggestedWaypointIDs: {
            type: Array,
            default: () => null,
        },
        statusOverrideLoading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['setIsCollapsed', 'addToItinerary', 'deleteWaypoint', 'openWaypointDetails'],
    setup(props, { emit }) {
        const portalStore = usePortalStore();
        const { viewAs } = portalStore;
        const waypointIndex = computed(() => { var _a; return portalStore.itinerary.getters.waypointIndexByID(((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.id) || ''); });
        const waypointInItinerary = computed(() => waypointIndex.value !== -1);
        const isReady = computed(() => portalStore.state.value.isReady);
        const isInvalid = computed(() => {
            var _a;
            return isReady.value
                && !props.dwPreview && props.waypoint
                && ((_a = portalStore.waypoints.get(props.waypoint.id)) === null || _a === void 0 ? void 0 : _a.computed.draftWaypointMismatched);
        });
        const suggestedWaypoints = computed(() => {
            var _a;
            return ((_a = props.suggestedWaypointIDs) !== null && _a !== void 0 ? _a : []).map((id) => ({
                id,
                letter: getWaypointLetter(portalStore.itinerary.getters.waypointIndexByID(id)),
            }));
        });
        const waypointLetter = computed(() => getWaypointLetter(waypointIndex.value));
        const waypointLetterClassExtension = computed(() => { var _a, _b; return (_b = portalStore.waypoints.get(((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.id) || '')) === null || _b === void 0 ? void 0 : _b.computed.waypointLetterClassExtension; });
        const waypointStatus = computed(() => {
            var _a, _b;
            let opacity;
            if (isInvalid.value) {
                opacity = 0.3;
                return {
                    color: hexAlphaTransform('#FEDF89', opacity),
                    text: 'WARNING: This was found on another created itinerary',
                    textColor: '#DC6803', // $cs-orange-400
                };
            }
            if (!((_a = props.waypoint) === null || _a === void 0 ? void 0 : _a.status.status)) {
                opacity = 0.3;
                return {
                    color: hexAlphaTransform('#D8DAE5', opacity),
                    text: 'Loading waypoint',
                    textColor: '#8F95B2', // $cs-grey-600
                };
            }
            // NOTE: We have decided to remove the "Skipped" status from the waypoint card.
            // if we ever bring it back, uncomment
            // const allWaypointActionsCanceled = portalStore.waypoints.getters.allWaypointActionsCanceled(props.waypoint);
            // if (
            //   allWaypointActionsCanceled
            //   && hasStatus(props.waypoint, WaypointStatusName.COMPLETE)
            // ) {
            //   opacity = 0.3;
            //   return {
            //     color: hexAlphaTransform('#D8DAE5', opacity), // $cs-grey-400 with opacity
            //     text: 'Skipped',
            //     textColor: '#8F95B2', // $cs-grey-600
            //   };
            // }
            const tagInfo = getTagInfoByFulfillmentStatus(props.waypoint.status.status);
            switch ((_b = props.waypoint) === null || _b === void 0 ? void 0 : _b.status.status) {
                case (WaypointStatusName.DRAFT):
                    return {
                        ...tagInfo.colors,
                        text: 'Commit itinerary to confirm',
                    };
                case (WaypointStatusName.CREATED):
                    return {
                        ...tagInfo.colors,
                        text: 'Created',
                    };
                case (WaypointStatusName.IN_ITINERARY):
                    return {
                        ...tagInfo.colors,
                        text: 'Confirmed',
                    };
                case (WaypointStatusName.AT_WAYPOINT):
                    return {
                        ...tagInfo.colors,
                        text: 'In progress',
                    };
                case (WaypointStatusName.TRANSIT_WAYPOINT):
                    return {
                        ...tagInfo.colors,
                        text: 'On the way',
                    };
                case (WaypointStatusName.COMPLETE):
                    return {
                        ...tagInfo.colors,
                        text: 'Complete',
                    };
            }
        });
        const addToItinerary = (existingWpID) => {
            emit('addToItinerary', existingWpID);
        };
        const setIsCollapsed = (isCollapsed) => {
            emit('setIsCollapsed', isCollapsed);
        };
        const infoIcon = computed(() => hasStatus(props.waypoint, WaypointStatusName.COMPLETE)
            ? 'check-circle'
            : 'info-circle');
        const isSuperCollapsed = computed(() => props.isCardCollapsed && hasStatus(props.waypoint, WaypointStatusName.COMPLETE));
        return {
            addToItinerary,
            infoIcon,
            setIsCollapsed,
            isSuperCollapsed,
            suggestedWaypoints,
            waypointInItinerary,
            waypointStatus,
            waypointLetter,
            waypointLetterClassExtension,
            viewAs,
        };
    },
});

export { script as default };
